import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

// The sidebar: sections with an icon and a chevron that fold, and their pages on a rail with a dot each; the
// page you are on has a filled dot. Folded sections are remembered; arriving at a page opens its section.

/** match: other paths that belong to this item (its section's tabs) */
export type NavItem = { to: string; label: string; badge?: number; match?: string[] };
export type NavGroup = { title: string; items: NavItem[] };

const p = (d: ReactNode) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{d}</svg>;
export const ICONS: Record<string, ReactNode> = {
  work: p(<><path d="M4 7h16v11a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2z" /><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" /><path d="M4 12h16" /></>),
  market: p(<><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="4" /><circle cx="12" cy="12" r="1" /></>),
  workspace: p(<><circle cx="12" cy="12" r="3" /><path d="M19 12a7 7 0 0 0-.1-1.2l2-1.6-2-3.4-2.4 1a7 7 0 0 0-2-1.2L14 3h-4l-.4 2.6a7 7 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.6a7 7 0 0 0 0 2.4l-2 1.6 2 3.4 2.4-1a7 7 0 0 0 2 1.2L10 21h4l.4-2.6a7 7 0 0 0 2-1.2l2.4 1 2-3.4-2-1.6c.1-.4.2-.8.2-1.2z" /></>),
  account: p(<><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></>),
  overview: p(<><rect x="3.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="3.5" width="7" height="7" rx="1.5" /><rect x="3.5" y="13.5" width="7" height="7" rx="1.5" /><rect x="13.5" y="13.5" width="7" height="7" rx="1.5" /></>),
  customers: p(<><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0 1 13 0" /><path d="M16 4.5a3.5 3.5 0 0 1 0 7M21.5 20a6.5 6.5 0 0 0-4-6" /></>),
  queues: p(<><path d="M4 6h16M4 12h16M4 18h10" /></>),
  compliance: p(<><path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6z" /><path d="M9 12l2 2 4-4" /></>),
  platform: p(<><rect x="3" y="4" width="18" height="6" rx="1.5" /><rect x="3" y="14" width="18" height="6" rx="1.5" /><path d="M7 7h.01M7 17h.01" /></>),
  growth: p(<><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>),
  content: p(<><path d="M6 3h9l4 4v14H6z" /><path d="M14 3v5h5M9 13h7M9 17h7" /></>),
  chats: p(<><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" /><path d="M8 11h8M8 14h5" /></>),
  campaigns: p(<><path d="M22 2L11 13" /><path d="M22 2l-7 20-4-9-9-4z" /></>),
  setup: p(<><path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18v3h3l6.3-6.3a4 4 0 0 0 5.4-5.4l-2.6 2.6-2.4-.6-.6-2.4z" /></>),
  // integrations: a plug
  plug: p(<><path d="M9 7V3M15 7V3" /><path d="M6 7h12v4a6 6 0 0 1-12 0z" /><path d="M12 17v4" /></>),
  settings: p(<><path d="M4 6h10M18 6h2M4 12h4M12 12h8M4 18h12M20 18h0" /><circle cx="16" cy="6" r="2" /><circle cx="10" cy="12" r="2" /><circle cx="18" cy="18" r="2" /></>),
};
const ICON_FOR: Record<string, string> = {
  Work: 'work', 'Go to market': 'market', Workspace: 'workspace', Account: 'account', Overview: 'overview', Customers: 'customers',
  Queues: 'queues', Campaigns: 'campaigns', Setup: 'setup', Compliance: 'compliance', Platform: 'platform', Growth: 'growth', Content: 'content', Settings: 'settings',
};

const KEY = 'olv_nav_folded';
const loadFolded = (): string[] => { try { return JSON.parse(localStorage.getItem(KEY) ?? '[]') as string[]; } catch { return []; } };

const matches = (path: string, to: string, end: boolean) => (end ? path === to : path === to || path.startsWith(`${to}/`));

// The ops menu starts with only its first section open (owner request 2026-10-23); what a person opens or closes is
// remembered in its own key. The customer menu keeps every section open until folded.
const OPS_KEY = 'olv_ops_nav_folded';
const loadFoldedFirstOnly = (groups: NavGroup[]): string[] => {
  try { const v = localStorage.getItem(OPS_KEY); if (v !== null) return JSON.parse(v) as string[]; } catch { /* private mode */ }
  return groups.slice(1).map((g) => g.title);
};

export function NavGroups({ groups, footer, firstOnly = false }: { groups: NavGroup[]; footer?: ReactNode; firstOnly?: boolean }) {
  const { pathname } = useLocation();
  const store = firstOnly ? OPS_KEY : KEY;
  const [folded, setFolded] = useState<string[]>(() => (firstOnly ? loadFoldedFirstOnly(groups) : loadFolded()));
  // the deepest match is the current page ('/ops' and '/' only match exactly)
  const hit = (i: NavItem) => Math.max(...[i.to, ...(i.match ?? [])].map((t) => (matches(pathname, t, t === '/' || t === '/ops') ? t.length : -1)));
  const current = groups.flatMap((g) => g.items).filter((i) => hit(i) >= 0).sort((a, b) => hit(b) - hit(a))[0]?.to;
  const toggle = (t: string) => setFolded((f) => { const n = f.includes(t) ? f.filter((x) => x !== t) : [...f, t]; try { localStorage.setItem(store, JSON.stringify(n)); } catch { /* private mode */ } return n; });
  // arriving at a page in a folded section opens it
  const home = groups.find((g) => g.items.some((i) => i.to === current))?.title;
  useEffect(() => { if (home && folded.includes(home)) toggle(home); }, [home]);
  return (
    <div className="navgroups">
      {groups.map((g) => {
        const open = !folded.includes(g.title);
        const id = `nav-${g.title.replace(/\W+/g, '-').toLowerCase()}`;
        return (
          <div key={g.title} className={`navgroup${open ? ' open' : ''}`}>
            <button type="button" className="navgroup-head" aria-expanded={open} aria-controls={id} onClick={() => toggle(g.title)}>
              <span className="navgroup-icon">{ICONS[ICON_FOR[g.title] ?? 'overview']}</span>
              <span className="navgroup-title">{g.title}</span>
              <svg className="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
            </button>
            {open ? (
              <ul id={id} className="rail">
                {g.items.map((i) => (
                  <li key={i.to}>
                    <NavLink to={i.to} end={i.to === '/' || i.to === '/ops'} aria-current={i.to === current ? 'page' : undefined} className={i.to === current ? 'current' : ''}>
                      <span className="dot" aria-hidden="true" />
                      <span className="label">{i.label}</span>
                      {i.badge ? <span className="badge" aria-label={`${i.badge} waiting`}>{i.badge}</span> : null}
                    </NavLink>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        );
      })}
      {footer}
    </div>
  );
}
